// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="Sheet" set="mytaible-shadcn-studio" verified=tbd

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Sheet (root) ─────────────────────────────────────────────────────────────

export interface SheetProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> {}

const Sheet = DialogPrimitive.Root;
Sheet.displayName = 'Sheet';

// ─── SheetTrigger ─────────────────────────────────────────────────────────────

export interface SheetTriggerProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger> {}

const SheetTrigger = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Trigger>,
  SheetTriggerProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Trigger ref={ref} className={cn(className)} {...props} />
));
SheetTrigger.displayName = 'SheetTrigger';

// ─── SheetClose ───────────────────────────────────────────────────────────────

export interface SheetCloseProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close> {}

const SheetClose = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Close>,
  SheetCloseProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Close ref={ref} className={cn(className)} {...props} />
));
SheetClose.displayName = 'SheetClose';

// ─── SheetPortal ──────────────────────────────────────────────────────────────

const SheetPortal = DialogPrimitive.Portal;
SheetPortal.displayName = 'SheetPortal';

// ─── SheetOverlay ─────────────────────────────────────────────────────────────

const SheetOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-foreground/20 backdrop-blur-[3px]',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      'data-[state=open]:duration-500 data-[state=closed]:duration-300',
      className,
    )}
    {...props}
  />
));
SheetOverlay.displayName = 'SheetOverlay';

// ─── SheetContent variants ────────────────────────────────────────────────────

const sheetContentVariants = cva('fixed z-50 flex flex-col bg-background shadow-lg', {
  variants: {
    side: {
      right:
        'inset-y-0 right-0 h-full w-3/4 max-w-sm border-l border-border transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
      left: 'inset-y-0 left-0 h-full w-3/4 max-w-sm border-r border-border transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
      top: 'inset-x-0 top-0 border-b border-border h-1/3 transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
      bottom:
        'inset-x-0 bottom-0 border-t border-border h-1/3 data-[state=open]:animate-sheet-spring-in data-[state=closed]:animate-sheet-spring-out',
    },
  },
  defaultVariants: {
    side: 'right',
  },
});

// ─── SheetContent ─────────────────────────────────────────────────────────────

export interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof sheetContentVariants> {
  /** Hide the built-in close button. Use when the caller provides its own close control. */
  hideCloseButton?: boolean;
}

const SheetContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ side, className, children, hideCloseButton, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(sheetContentVariants({ side }), className)}
      {...props}
    >
      {!hideCloseButton && (
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-sm text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          aria-label="Close"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </DialogPrimitive.Close>
      )}
      {children}
    </DialogPrimitive.Content>
  </SheetPortal>
));
SheetContent.displayName = 'SheetContent';

// ─── SheetHeader ──────────────────────────────────────────────────────────────

export interface SheetHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

const SheetHeader = React.forwardRef<HTMLDivElement, SheetHeaderProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1.5 p-6 pb-0', className)} {...props} />
  ),
);
SheetHeader.displayName = 'SheetHeader';

// ─── SheetFooter ──────────────────────────────────────────────────────────────

export interface SheetFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

const SheetFooter = React.forwardRef<HTMLDivElement, SheetFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex flex-col-reverse gap-2 p-6 pt-0 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  ),
);
SheetFooter.displayName = 'SheetFooter';

// ─── SheetTitle ───────────────────────────────────────────────────────────────

export interface SheetTitleProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> {}

const SheetTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  SheetTitleProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold text-foreground', className)}
    {...props}
  />
));
SheetTitle.displayName = 'SheetTitle';

// ─── SheetDescription ─────────────────────────────────────────────────────────

export interface SheetDescriptionProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> {}

const SheetDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  SheetDescriptionProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
SheetDescription.displayName = 'SheetDescription';

// ─── Exports ──────────────────────────────────────────────────────────────────

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
};
